import {
    ISignalsSelectionTypes,
    SignalsChartType,
    SignalsEndpoints,
} from "components/Signals/SignalsModels";
import type {
    Module,
    Question,
    Tag,
} from "components/Admin/Organizations/types/Module.type";
import type Classifier from "models/ClassifierModel";
import type ClassifierCategory from "models/ClassifierCategoryId";
import type {
    SlimExtractionLLMClassifierResponse,
    SlimLLMClassifierResponse,
} from "models/LLMs";
import type { UnMappedCustomerType } from "models/CustomerType";
import { normalizeClassifierCategoryId } from "../ChartCard/ChartControls/classifierUtils";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import { filterModulesByCategory } from "./moduleSelector";
import type { ModuleSelectorConfig } from "./Types";

export type SelectionDefaultDependency =
    | "modules"
    | "classifiers"
    | "llmClassifiers"
    | "customerTypes"
    | "hipaaOptions"
    | "extractionLlmClassifiers";

export type SelectedIdsSnapshot = {
    llmClassifier?: string;
    classifierCategory?: string;
    classifiers?: string | string[];
    question?: string;
    module?: string;
    modules?: string | string[];
    questions?: string | string[];
    tags?: string | string[];
    topics?: string | string[];
    themes?: string | string[];
    extractionLlmClassifier?: string;
    extendedMetadataObjectClassifier?: string;
    /** Ordered classifier group path [root, ..., leaf]. */
    classifierGroup?: string | string[];
};

export type SelectionDefaultsPatch = Partial<SelectedIdsSnapshot>;

export type SelectionDefaultsResolverContext = {
    selectionType: ISignalsSelectionTypes;
    chartType: SignalsChartType;
    dataEndpoint?: string;
    maxSelection: number;
    /** How many items to auto-select when nothing is saved; one chart page. */
    autoSelectionLimit: number;
    preferVizDefaults: boolean;
    vizDefaultSelectedIds: string[];
    vizSelectedClassifierCategoryId?: string;
    selectedIds: SelectedIdsSnapshot;
    attemptedDependencies: ReadonlySet<SelectionDefaultDependency>;
    options: {
        orgModules: Module[];
        orgScoredModules: Module[];
        userClassifiers: Classifier[];
        classifierCategories: ClassifierCategory[];
        llmClassifiers: SlimLLMClassifierResponse[];
        customerTypes: UnMappedCustomerType[];
        topics: Array<{ id: string; topicLabel?: string }>;
        themes: Array<{ id: string; themeLabel?: string }>;
        hipaaOptions: Array<{ id: string }>;
        extractionLlmClassifiers: SlimExtractionLLMClassifierResponse[];
        extendedMetadataObjectClassifiers: ExtendedMetadataClassifier[];
    };
    loading: {
        orgModulesLoading: boolean;
        userClassifiersLoading: boolean;
        classifierCategoriesLoading: boolean;
        llmClassifiersLoading: boolean;
        customerTypesLoading: boolean;
        hipaaOptionsLoading: boolean;
        extractionLlmClassifiersLoading: boolean;
        extendedMetadataObjectClassifiersLoading: boolean;
    };
    capabilities: {
        canSelectAllModules: boolean;
    };
    /**
     * Endpoint category scoping for the module pool, so the resolved default comes from the same
     * modules the dropdown offers. Undefined leaves the pool unscoped.
     */
    moduleSelector?: ModuleSelectorConfig;
};

export type SelectionDefaultsReadyResult = {
    status: "ready";
    patch: SelectionDefaultsPatch;
    dataGridSelectionIds?: string[];
    source: "existing" | "viz-options" | "auto";
    autoFallbackUsed: boolean;
};

export type SelectionDefaultsWaitingResult = {
    status: "waiting";
    dependencies: SelectionDefaultDependency[];
};

export type SelectionDefaultsResolveResult =
    | SelectionDefaultsReadyResult
    | SelectionDefaultsWaitingResult;

const SAFETY_EVENT_DEFAULT_SELECTIONS = [
    "Identified",
    "Not Identified",
    "Acknowledged",
    "Not Acknowledged",
];

type DependencyResolution = "ready" | "waiting" | "resolved-empty";

type SelectionSource = "existing" | "viz-options" | "auto";

type SelectionChoice = {
    ids: string[];
    source: SelectionSource;
    explicitEmpty: boolean;
};

type SelectionModule = Pick<
    Module,
    "id" | "name" | "questions" | "moduleCategoryName"
>;

type SelectionQuestion = Pick<Question, "id" | "tags">;

type SelectionTag = Pick<Tag, "id">;

const normalizeIds = (value?: string | string[]): string[] => {
    if (Array.isArray(value)) {
        return value.map((id) => `${id}`.trim()).filter((id) => id.length > 0);
    }

    if (!value) {
        return [];
    }

    const normalized = `${value}`.trim();
    return normalized.length ? [normalized] : [];
};

const dedupe = (ids: string[]): string[] => Array.from(new Set(ids));

const toSortedByName = (modules: SelectionModule[]): SelectionModule[] =>
    [...modules].sort((left, right) =>
        `${left.name ?? ""}`.localeCompare(`${right.name ?? ""}`),
    );

const isPublishedModelResultsEndpoint = (endpoint?: string): boolean => {
    if (!endpoint) return false;

    return (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsCountId,
        ) ||
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsOverTimeId,
        )
    );
};

export const getEndpointQueryParam = (
    endpoint: string | undefined,
    paramName: string,
): string | undefined => {
    if (!endpoint) return undefined;
    const query = endpoint.split("?")[1];
    if (!query) return undefined;
    const params = new URLSearchParams(query);
    const value = params.get(paramName);
    return value ?? undefined;
};

export const getPublishedModelResultsClassifierIdFromEndpoint = (
    endpoint: string | undefined,
): string | undefined => {
    if (!isPublishedModelResultsEndpoint(endpoint)) {
        return undefined;
    }

    return getEndpointQueryParam(endpoint, "classifierId") ?? undefined;
};

const getDependencyResolution = (
    dependency: SelectionDefaultDependency,
    context: SelectionDefaultsResolverContext,
): DependencyResolution => {
    switch (dependency) {
        case "modules": {
            const hasAny =
                context.options.orgModules.length > 0 ||
                context.options.orgScoredModules.length > 0;
            if (hasAny) return "ready";
            if (context.loading.orgModulesLoading) return "waiting";
            return context.attemptedDependencies.has("modules")
                ? "resolved-empty"
                : "waiting";
        }
        case "classifiers": {
            const hasAny =
                context.options.userClassifiers.length > 0 ||
                context.options.classifierCategories.length > 0;
            if (hasAny) return "ready";
            if (
                context.loading.userClassifiersLoading ||
                context.loading.classifierCategoriesLoading
            ) {
                return "waiting";
            }
            return context.attemptedDependencies.has("classifiers")
                ? "resolved-empty"
                : "waiting";
        }
        case "llmClassifiers": {
            const hasAny = context.options.llmClassifiers.length > 0;
            if (hasAny) return "ready";
            if (context.loading.llmClassifiersLoading) return "waiting";
            return context.attemptedDependencies.has("llmClassifiers")
                ? "resolved-empty"
                : "waiting";
        }
        case "customerTypes": {
            const hasAny = context.options.customerTypes.length > 0;
            if (hasAny) return "ready";
            if (context.loading.customerTypesLoading) return "waiting";
            return context.attemptedDependencies.has("customerTypes")
                ? "resolved-empty"
                : "waiting";
        }
        case "hipaaOptions": {
            const hasAny = context.options.hipaaOptions.length > 0;
            if (hasAny) return "ready";
            if (context.loading.hipaaOptionsLoading) return "waiting";
            return context.attemptedDependencies.has("hipaaOptions")
                ? "resolved-empty"
                : "waiting";
        }
        case "extractionLlmClassifiers": {
            if (
                context.loading.extractionLlmClassifiersLoading ||
                context.loading.extendedMetadataObjectClassifiersLoading
            ) {
                return "waiting";
            }
            return "ready";
        }
        default:
            return "ready";
    }
};

const getRequiredDependencies = (
    selectionType: ISignalsSelectionTypes,
): SelectionDefaultDependency[] => {
    switch (selectionType) {
        case "Question":
        case "Questions":
        case "Tags":
        case "Module":
        case "Modules":
            return ["modules"];
        case "Classifiers":
            return ["classifiers"];
        case "LLM Classifier":
        case "LLM Classifier Results":
            return ["llmClassifiers"];
        case "Contact Type":
            return ["customerTypes"];
        case "HIPAA":
            return ["hipaaOptions"];
        case "LLM Classifier Extracted Metadata":
            return ["extractionLlmClassifiers"];
        default:
            return [];
    }
};

const shouldUseAllModules = (
    context: SelectionDefaultsResolverContext,
): boolean =>
    context.selectionType === "Tags" ||
    context.capabilities.canSelectAllModules;

const isModuleCompatibleForSelectionType = (
    module: SelectionModule,
    selectionType: ISignalsSelectionTypes,
): boolean => {
    if (selectionType === "Question" || selectionType === "Questions") {
        return (module.questions?.length ?? 0) > 0;
    }

    if (selectionType === "Tags") {
        return (
            module.questions?.some(
                (question) => (question.tags?.length ?? 0) > 0,
            ) ?? false
        );
    }

    return true;
};

const hasCompatibleModules = (
    modules: SelectionModule[],
    selectionType: ISignalsSelectionTypes,
): boolean =>
    modules.some((module) =>
        isModuleCompatibleForSelectionType(module, selectionType),
    );

const getModulePool = (
    context: SelectionDefaultsResolverContext,
): SelectionModule[] => {
    const orgModules = context.options.orgModules ?? [];
    const scoredModules = context.options.orgScoredModules ?? [];

    const primary = shouldUseAllModules(context)
        ? orgModules
        : scoredModules.length
            ? scoredModules
            : orgModules;

    const secondary = primary === orgModules ? scoredModules : orgModules;

    const resolved = (() => {
        if (!primary.length && secondary.length) {
            return secondary;
        }

        if (
            (context.selectionType === "Question" ||
                context.selectionType === "Questions" ||
                context.selectionType === "Tags") &&
            !hasCompatibleModules(primary, context.selectionType) &&
            hasCompatibleModules(secondary, context.selectionType)
        ) {
            return secondary;
        }

        return primary;
    })();

    // Category scoping is applied last and is never fallen back from: the widening above exists to
    // find *some* usable catalog, but an endpoint that asks for one category must not silently
    // default to a module outside it. An empty result stays empty, which leaves the chart with no
    // module and the standard empty state.
    return filterModulesByCategory(resolved, context.moduleSelector);
};

const getPreferredFallbackModule = (
    modules: SelectionModule[],
    selectionType: ISignalsSelectionTypes,
): SelectionModule | undefined => {
    const compatibleModules = modules.filter((module) =>
        isModuleCompatibleForSelectionType(module, selectionType),
    );
    const modulesToUse = compatibleModules.length ? compatibleModules : modules;

    const qualityModule = modulesToUse.find(
        (module) => `${module.name ?? ""}`.toLowerCase() === "quality",
    );

    if (qualityModule) {
        return qualityModule;
    }

    return toSortedByName(modulesToUse)[0];
};

type BuildChoiceOptions = {
    currentValue: string | string[] | undefined;
    validIds: Set<string>;
    maxSelection: number;
    vizDefaultIds: string[];
    autoFallbackIds: string[];
    preferVizDefaults?: boolean;
};

const buildChoice = ({
    currentValue,
    validIds,
    maxSelection,
    vizDefaultIds,
    autoFallbackIds,
    preferVizDefaults = false,
}: BuildChoiceOptions): SelectionChoice => {
    const currentExplicitArray = Array.isArray(currentValue);
    const currentIds = normalizeIds(currentValue);
    const validatedCurrent = dedupe(currentIds)
        .filter((id) => validIds.has(id))
        .slice(0, maxSelection);

    const validatedViz = dedupe(vizDefaultIds)
        .filter((id) => validIds.has(id))
        .slice(0, maxSelection);

    const getExistingChoice = (): SelectionChoice | undefined => {
        if (validatedCurrent.length > 0) {
            return {
                ids: validatedCurrent,
                source: "existing",
                explicitEmpty: false,
            };
        }

        if (currentExplicitArray && currentIds.length === 0) {
            return {
                ids: [],
                source: "existing",
                explicitEmpty: true,
            };
        }

        return undefined;
    };

    const getVizChoice = (): SelectionChoice | undefined => {
        if (validatedViz.length > 0) {
            return {
                ids: validatedViz,
                source: "viz-options",
                explicitEmpty: false,
            };
        }

        return undefined;
    };

    if (preferVizDefaults) {
        const vizChoice = getVizChoice();
        if (vizChoice) {
            return vizChoice;
        }

        const existingChoice = getExistingChoice();
        if (existingChoice) {
            return existingChoice;
        }
    } else {
        const existingChoice = getExistingChoice();
        if (existingChoice) {
            return existingChoice;
        }

        const vizChoice = getVizChoice();
        if (vizChoice) {
            return vizChoice;
        }
    }

    const validatedFallback = dedupe(autoFallbackIds)
        .filter((id) => validIds.has(id))
        .slice(0, maxSelection);

    return {
        ids: validatedFallback,
        source: validatedFallback.length > 0 ? "auto" : "existing",
        explicitEmpty: validatedFallback.length === 0,
    };
};

const getSourcePriority = (source: SelectionSource): number => {
    switch (source) {
        case "viz-options":
            return 3;
        case "auto":
            return 2;
        default:
            return 1;
    }
};

const combineSources = (
    sources: SelectionSource[],
): { source: SelectionSource; autoFallbackUsed: boolean } => {
    const autoFallbackUsed = sources.includes("auto");

    if (!sources.length) {
        return { source: "existing", autoFallbackUsed: false };
    }

    const primary = [...sources].sort(
        (left, right) => getSourcePriority(right) - getSourcePriority(left),
    )[0];

    return {
        source: primary,
        autoFallbackUsed,
    };
};

const resolveModuleSelection = (
    context: SelectionDefaultsResolverContext,
    modules: SelectionModule[],
): { moduleId?: string; source: SelectionSource } => {
    const validModuleIds = new Set(modules.map((module) => module.id));
    const currentModuleId = context.selectedIds.module;

    const resolveVizModule = (): string | undefined => {
        const firstVizId = context.vizDefaultSelectedIds[0];

        if (
            firstVizId &&
            (context.selectionType === "Question" ||
                context.selectionType === "Questions")
        ) {
            const moduleForQuestion = modules.find((module) =>
                module.questions?.some(
                    (question) => question.id === firstVizId,
                ),
            );

            if (moduleForQuestion?.id) {
                return moduleForQuestion.id;
            }
        }

        if (firstVizId && context.selectionType === "Tags") {
            const moduleForTag = modules.find((module) =>
                module.questions?.some((question) =>
                    question.tags?.some((tag) => tag.id === firstVizId),
                ),
            );

            if (moduleForTag?.id) {
                return moduleForTag.id;
            }
        }

        if (context.selectionType === "Module") {
            const vizModuleId = context.vizDefaultSelectedIds[0];
            if (vizModuleId && validModuleIds.has(vizModuleId)) {
                return vizModuleId;
            }
        }

        return undefined;
    };

    if (context.preferVizDefaults) {
        const vizModuleId = resolveVizModule();
        if (vizModuleId) {
            return {
                moduleId: vizModuleId,
                source: "viz-options",
            };
        }
    }

    if (currentModuleId && validModuleIds.has(currentModuleId)) {
        return {
            moduleId: currentModuleId,
            source: "existing",
        };
    }

    if (!context.preferVizDefaults) {
        const vizModuleId = resolveVizModule();
        if (vizModuleId) {
            return {
                moduleId: vizModuleId,
                source: "viz-options",
            };
        }
    }

    const fallbackModule = getPreferredFallbackModule(
        modules,
        context.selectionType,
    );

    return {
        moduleId: fallbackModule?.id,
        source: fallbackModule?.id ? "auto" : "existing",
    };
};

const getQuestionsForModule = (
    modules: SelectionModule[],
    moduleId?: string,
): SelectionQuestion[] => {
    return modules.find((module) => module.id === moduleId)?.questions ?? [];
};

const getTagsForQuestion = (
    questions: SelectionQuestion[],
    questionId?: string,
): SelectionTag[] => {
    return questions.find((question) => question.id === questionId)?.tags ?? [];
};

const resolveQuestionSingleSelection = (
    context: SelectionDefaultsResolverContext,
    modules: SelectionModule[],
    moduleId?: string,
): { questionId?: string; source: SelectionSource } => {
    const questions = getQuestionsForModule(modules, moduleId);
    const validQuestionIds = new Set(
        questions
            .map((question) => question.id)
            .filter((id): id is string => !!id),
    );

    const resolveVizQuestion = (): string | undefined => {
        if (context.selectionType === "Question") {
            const vizQuestionId = context.vizDefaultSelectedIds[0];
            if (vizQuestionId && validQuestionIds.has(vizQuestionId)) {
                return vizQuestionId;
            }
        }

        if (context.selectionType === "Tags") {
            const vizTagId = context.vizDefaultSelectedIds[0];
            if (vizTagId) {
                const questionContainingTag = questions.find((question) =>
                    question.tags?.some((tag) => tag.id === vizTagId),
                );

                if (questionContainingTag?.id) {
                    return questionContainingTag.id;
                }
            }
        }

        return undefined;
    };

    if (context.preferVizDefaults) {
        const vizQuestionId = resolveVizQuestion();
        if (vizQuestionId) {
            return {
                questionId: vizQuestionId,
                source: "viz-options",
            };
        }
    }

    const currentQuestionId = context.selectedIds.question;
    if (currentQuestionId && validQuestionIds.has(currentQuestionId)) {
        return {
            questionId: currentQuestionId,
            source: "existing",
        };
    }

    if (!context.preferVizDefaults) {
        const vizQuestionId = resolveVizQuestion();
        if (vizQuestionId) {
            return {
                questionId: vizQuestionId,
                source: "viz-options",
            };
        }
    }

    return {
        questionId: questions[0]?.id,
        source: questions[0]?.id ? "auto" : "existing",
    };
};

const resolveClassifiers = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const categories = context.options.classifierCategories ?? [];
    const allClassifiers = context.options.userClassifiers ?? [];

    const currentCategoryId = normalizeClassifierCategoryId(
        context.selectedIds.classifierCategory,
    );
    const vizCategoryId = normalizeClassifierCategoryId(
        context.vizSelectedClassifierCategoryId,
    );

    const isValidCategory = (categoryId?: string): boolean =>
        !categoryId ||
        categories.some((category) => category.id === categoryId);

    const nextCategoryId = context.preferVizDefaults
        ? isValidCategory(vizCategoryId)
            ? vizCategoryId
            : isValidCategory(currentCategoryId)
                ? currentCategoryId
                : undefined
        : isValidCategory(currentCategoryId)
            ? currentCategoryId
            : isValidCategory(vizCategoryId)
                ? vizCategoryId
                : undefined;

    const scopedClassifiers = nextCategoryId
        ? allClassifiers.filter(
            (classifier) =>
                classifier.classifierCategory?.id === nextCategoryId,
        )
        : allClassifiers;

    const validClassifierIds = new Set(
        scopedClassifiers.map((classifier) => `${classifier.id}`),
    );

    const choice = buildChoice({
        currentValue: context.selectedIds.classifiers,
        validIds: validClassifierIds,
        maxSelection: context.maxSelection,
        vizDefaultIds: context.vizDefaultSelectedIds,
        autoFallbackIds: scopedClassifiers
            .map((classifier) => `${classifier.id}`)
            .slice(0, context.autoSelectionLimit),
        preferVizDefaults: context.preferVizDefaults,
    });

    const classifierIds = choice.source === "auto" ? [] : choice.ids;
    const patch: SelectionDefaultsPatch = {
        classifierCategory: nextCategoryId,
        classifiers: classifierIds,
    };

    return {
        status: "ready",
        patch,
        source: choice.source,
        autoFallbackUsed: choice.source === "auto",
    };
};

const resolveLLMClassifier = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const llmOptions = context.options.llmClassifiers ?? [];
    const validIds = new Set(llmOptions.map((classifier) => classifier.id));

    const endpointClassifierId =
        getPublishedModelResultsClassifierIdFromEndpoint(context.dataEndpoint);

    const current = context.selectedIds.llmClassifier;
    if (
        endpointClassifierId &&
        validIds.has(endpointClassifierId) &&
        current !== endpointClassifierId
    ) {
        return {
            status: "ready",
            patch: {
                llmClassifier: endpointClassifierId,
            },
            source: "auto",
            autoFallbackUsed: true,
        };
    }

    const vizDefault = context.vizDefaultSelectedIds[0];
    const hasValidVizDefault = !!vizDefault && validIds.has(vizDefault);
    const hasValidCurrent = !!current && validIds.has(current);

    if (context.preferVizDefaults) {
        if (hasValidVizDefault) {
            return {
                status: "ready",
                patch: {
                    llmClassifier: vizDefault,
                },
                source: "viz-options",
                autoFallbackUsed: false,
            };
        }

        if (hasValidCurrent) {
            return {
                status: "ready",
                patch: {},
                source: "existing",
                autoFallbackUsed: false,
            };
        }
    } else {
        if (hasValidCurrent) {
            return {
                status: "ready",
                patch: {},
                source: "existing",
                autoFallbackUsed: false,
            };
        }

        if (hasValidVizDefault) {
            return {
                status: "ready",
                patch: {
                    llmClassifier: vizDefault,
                },
                source: "viz-options",
                autoFallbackUsed: false,
            };
        }
    }

    const fallback = llmOptions[0]?.id;

    return {
        status: "ready",
        patch: {
            llmClassifier: fallback,
        },
        source: fallback ? "auto" : "existing",
        autoFallbackUsed: !!fallback,
    };
};

const resolveModulesMulti = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const modules = getModulePool(context);
    const validModuleIds = new Set(modules.map((module) => module.id));

    const choice = buildChoice({
        currentValue: context.selectedIds.modules,
        validIds: validModuleIds,
        maxSelection: context.maxSelection,
        vizDefaultIds: context.vizDefaultSelectedIds,
        autoFallbackIds: modules.map((module) => module.id).slice(0, context.autoSelectionLimit),
        preferVizDefaults: context.preferVizDefaults,
    });

    return {
        status: "ready",
        patch: {
            modules: choice.ids,
        },
        dataGridSelectionIds: choice.ids,
        source: choice.source,
        autoFallbackUsed: choice.source === "auto",
    };
};

const resolveModuleSingle = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const modules = getModulePool(context);
    const moduleSelection = resolveModuleSelection(context, modules);

    return {
        status: "ready",
        patch: {
            module: moduleSelection.moduleId,
        },
        source: moduleSelection.source,
        autoFallbackUsed: moduleSelection.source === "auto",
    };
};

const resolveQuestionSingle = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const modules = getModulePool(context);
    const moduleSelection = resolveModuleSelection(context, modules);
    const questionSelection = resolveQuestionSingleSelection(
        context,
        modules,
        moduleSelection.moduleId,
    );

    const { source, autoFallbackUsed } = combineSources([
        moduleSelection.source,
        questionSelection.source,
    ]);

    return {
        status: "ready",
        patch: {
            module: moduleSelection.moduleId,
            question: questionSelection.questionId,
        },
        source,
        autoFallbackUsed,
    };
};

const resolveQuestionsMulti = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const modules = getModulePool(context);
    const moduleSelection = resolveModuleSelection(context, modules);
    const questions = getQuestionsForModule(modules, moduleSelection.moduleId);

    const validQuestionIds = new Set(
        questions
            .map((question) => question.id)
            .filter((id): id is string => !!id),
    );

    const choice = buildChoice({
        currentValue: context.selectedIds.questions,
        validIds: validQuestionIds,
        maxSelection: context.maxSelection,
        vizDefaultIds: context.vizDefaultSelectedIds,
        autoFallbackIds: questions
            .map((question) => question.id)
            .filter((id): id is string => !!id)
            .slice(0, context.autoSelectionLimit),
        preferVizDefaults: context.preferVizDefaults,
    });

    const { source, autoFallbackUsed } = combineSources([
        moduleSelection.source,
        choice.source,
    ]);

    return {
        status: "ready",
        patch: {
            module: moduleSelection.moduleId,
            questions: choice.ids,
        },
        dataGridSelectionIds: choice.ids,
        source,
        autoFallbackUsed,
    };
};

const resolveTags = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const modules = getModulePool(context);
    const moduleSelection = resolveModuleSelection(context, modules);
    const questionSelection = resolveQuestionSingleSelection(
        context,
        modules,
        moduleSelection.moduleId,
    );

    const questions = getQuestionsForModule(modules, moduleSelection.moduleId);
    const tags = getTagsForQuestion(questions, questionSelection.questionId);
    const validTagIds = new Set(
        tags.map((tag) => tag.id).filter((id): id is string => !!id),
    );

    const choice = buildChoice({
        currentValue: context.selectedIds.tags,
        validIds: validTagIds,
        maxSelection: context.maxSelection,
        vizDefaultIds: context.vizDefaultSelectedIds,
        autoFallbackIds: tags
            .map((tag) => tag.id)
            .filter((id): id is string => !!id)
            .slice(0, context.autoSelectionLimit),
        preferVizDefaults: context.preferVizDefaults,
    });

    const { source, autoFallbackUsed } = combineSources([
        moduleSelection.source,
        questionSelection.source,
        choice.source,
    ]);

    return {
        status: "ready",
        patch: {
            module: moduleSelection.moduleId,
            question: questionSelection.questionId,
            tags: choice.ids,
        },
        dataGridSelectionIds: choice.ids,
        source,
        autoFallbackUsed,
    };
};

const resolveDataDerivedSelection = (
    context: SelectionDefaultsResolverContext,
    key: "topics" | "themes",
    options: Array<{ id: string }>,
): SelectionDefaultsReadyResult => {
    if (!options.length) {
        const fallbackViz = context.vizDefaultSelectedIds.slice(
            0,
            context.maxSelection,
        );
        const current = context.selectedIds[key];
        const currentIds = current !== undefined ? normalizeIds(current) : [];

        if (context.preferVizDefaults && fallbackViz.length > 0) {
            return {
                status: "ready",
                patch: {
                    [key]: fallbackViz,
                },
                source: "viz-options",
                autoFallbackUsed: false,
            };
        }

        if (current !== undefined) {
            return {
                status: "ready",
                patch: {
                    [key]: currentIds,
                },
                source: "existing",
                autoFallbackUsed: false,
            };
        }

        return {
            status: "ready",
            patch: fallbackViz.length
                ? {
                    [key]: fallbackViz,
                }
                : {},
            source: fallbackViz.length ? "viz-options" : "existing",
            autoFallbackUsed: false,
        };
    }

    // Options only hold what the last response returned, so keep current and saved ids too.
    // Helps if an option would get dropped when the filters change
    const validIds = new Set([
        ...options.map((option) => `${option.id}`),
        ...normalizeIds(context.selectedIds[key]),
        ...context.vizDefaultSelectedIds,
    ]);
    const choice = buildChoice({
        currentValue: context.selectedIds[key],
        validIds,
        maxSelection: context.maxSelection,
        vizDefaultIds: context.vizDefaultSelectedIds,
        autoFallbackIds: options
            .map((option) => `${option.id}`)
            .slice(0, context.autoSelectionLimit),
        preferVizDefaults: context.preferVizDefaults,
    });
    const selectedIds = choice.source === "auto" ? [] : choice.ids;

    return {
        status: "ready",
        patch: {
            [key]: selectedIds,
        },
        source: choice.source,
        autoFallbackUsed: choice.source === "auto",
    };
};

const resolveHipaa = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const selectionIds = context.options.hipaaOptions.map(
        (option) => option.id,
    );

    return {
        status: "ready",
        patch: {},
        dataGridSelectionIds: selectionIds,
        source: "auto",
        autoFallbackUsed: false,
    };
};

const resolveSafetyEvent = (): SelectionDefaultsReadyResult => {
    return {
        status: "ready",
        patch: {},
        dataGridSelectionIds: SAFETY_EVENT_DEFAULT_SELECTIONS,
        source: "auto",
        autoFallbackUsed: false,
    };
};

const resolveContactType = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const sourceNames = context.options.customerTypes
        .map((customerType) => `${customerType.sourceName ?? ""}`.trim())
        .filter((sourceName) => sourceName.length > 0);

    return {
        status: "ready",
        patch: {},
        dataGridSelectionIds: sourceNames,
        source: "auto",
        autoFallbackUsed: false,
    };
};

const resolveLLMClassifierExtractedMetadata = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const llmClassifierOptions = context.options.extractionLlmClassifiers ?? [];
    const extendedMetadataObjectOptions =
        context.options.extendedMetadataObjectClassifiers ?? [];

    const [defaultLlmClassifierId, defaultExtendedMetadataClassifierId] =
        context.vizDefaultSelectedIds;

    // Resolve extractionLlmClassifier
    const validExtractionIds = new Set(llmClassifierOptions.map((c) => c.id));

    const extractionChoice = buildChoice({
        currentValue: context.selectedIds.extractionLlmClassifier,
        validIds: validExtractionIds,
        maxSelection: 1,
        vizDefaultIds: [defaultLlmClassifierId],
        autoFallbackIds: llmClassifierOptions.slice(0, 1).map((c) => c.id),
        preferVizDefaults: context.preferVizDefaults,
    });
    const selectedExtractionId = extractionChoice.ids[0];

    // Resolve extendedMetadataObjectClassifier based on the selected extraction classifier
    const extractionClassifier = llmClassifierOptions.find(
        (c) => c.id === selectedExtractionId,
    );
    const potentialIds =
        extractionClassifier?.potentialExtendedMetadataClassifierIds ?? [];
    const validObjectIds = new Set(
        extendedMetadataObjectOptions
            .filter((c) => potentialIds.includes(c.id))
            .map((c) => c.id),
    );
    const firstValidObjectId = extendedMetadataObjectOptions.find((c) =>
        potentialIds.includes(c.id),
    )?.id;

    const objectChoice = buildChoice({
        currentValue: context.selectedIds.extendedMetadataObjectClassifier,
        validIds: validObjectIds,
        maxSelection: 1,
        vizDefaultIds: [defaultExtendedMetadataClassifierId],
        autoFallbackIds: firstValidObjectId ? [firstValidObjectId] : [],
        preferVizDefaults: context.preferVizDefaults,
    });

    const { source, autoFallbackUsed } = combineSources([
        extractionChoice.source,
        objectChoice.source,
    ]);

    return {
        status: "ready",
        patch: {
            extractionLlmClassifier: extractionChoice.ids[0],
            extendedMetadataObjectClassifier: objectChoice.ids[0],
        },
        source,
        autoFallbackUsed,
    };
};

const resolveFallbackSelection = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    return {
        status: "ready",
        patch: {},
        source: "existing",
        autoFallbackUsed: false,
    };
};

/**
 * Resolves the classifier group selection (ordered group path [root, ..., leaf]).
 *
 * This resolver runs on every refresh, so it must honor a live user selection —
 * otherwise each refresh would revert the chart to the persisted defaults. Like
 * the other resolvers: prefer viz defaults only on the initial hydration pass
 * (`preferVizDefaults`), then let the current selection win.
 *
 * There is no group tree in the resolver context (the selector self-fetches), so
 * the path is taken as-is; RecursiveGroupSelector validates/repairs it against
 * the live tree once it loads.
 */
const resolveClassifierGroup = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsReadyResult => {
    const currentPath = normalizeIds(context.selectedIds.classifierGroup);
    const vizPath = dedupe(context.vizDefaultSelectedIds).filter(
        (id) => id.length > 0,
    );

    if (context.preferVizDefaults && vizPath.length) {
        return {
            status: "ready",
            patch: { classifierGroup: vizPath },
            source: "viz-options",
            autoFallbackUsed: false,
        };
    }

    if (currentPath.length) {
        return {
            status: "ready",
            patch: { classifierGroup: currentPath },
            source: "existing",
            autoFallbackUsed: false,
        };
    }

    return {
        status: "ready",
        patch: vizPath.length ? { classifierGroup: vizPath } : {},
        source: vizPath.length ? "viz-options" : "existing",
        autoFallbackUsed: false,
    };
};

export const resolveSelectionDefaults = (
    context: SelectionDefaultsResolverContext,
): SelectionDefaultsResolveResult => {
    const requiredDependencies = getRequiredDependencies(context.selectionType);

    if (requiredDependencies.length > 0) {
        const waitingDependencies = requiredDependencies.filter(
            (dependency) =>
                getDependencyResolution(dependency, context) === "waiting",
        );

        if (waitingDependencies.length > 0) {
            return {
                status: "waiting",
                dependencies: waitingDependencies,
            };
        }
    }

    switch (context.selectionType) {
        case "Classifiers":
            return resolveClassifiers(context);
        case "LLM Classifier":
        case "LLM Classifier Results":
            return resolveLLMClassifier(context);
        case "Modules":
            return resolveModulesMulti(context);
        case "Module":
            return resolveModuleSingle(context);
        case "Question":
            return resolveQuestionSingle(context);
        case "Questions":
            return resolveQuestionsMulti(context);
        case "Tags":
            return resolveTags(context);
        case "Topics":
            return resolveDataDerivedSelection(
                context,
                "topics",
                context.options.topics ?? [],
            );
        case "Themes":
            return resolveDataDerivedSelection(
                context,
                "themes",
                context.options.themes ?? [],
            );
        case "HIPAA":
            return resolveHipaa(context);
        case "Safety Event":
            return resolveSafetyEvent();
        case "Contact Type":
            return resolveContactType(context);
        case "LLM Classifier Extracted Metadata":
            return resolveLLMClassifierExtractedMetadata(context);
        case "Classifier Group":
            return resolveClassifierGroup(context);
        default:
            return resolveFallbackSelection(context);
    }
};
